import type { FmStoryAction } from '../elements/elementFields.types';
import type {
  FmCopyElement,
  FmElementOf,
  FmElementType,
  FmNoticeElement,
} from '../elements/elements.types';

/**
 * The section state union: ready, partial and stale carry content (partial and
 * stale add a section-level notice); loading, empty, locked and error replace
 * it. An empty or locked state may carry the ONE owned next step that would
 * supply what is missing (confidence-ladder rule).
 */
export type FmSectionState =
  | { status: 'ready' }
  | { status: 'loading'; label: string }
  | { status: 'empty'; label: string; explanation: string; action?: FmStoryAction }
  | {
      status: 'partial';
      label: string;
      explanation: string;
      missingFieldsLabel: string;
      missingFields: string[];
    }
  | ({
      status: 'stale';
      label: string;
      explanation: string;
      observedAtLabel: string;
      observedAt: string;
    } & (
      | { refreshLabel: string; onRefresh: () => void }
      | { refreshLabel?: never; onRefresh?: never }
    ))
  | { status: 'locked'; label: string; explanation: string; action?: FmStoryAction }
  | ({ status: 'error'; label: string; explanation: string } & (
      | { retryLabel: string; onRetry: () => void }
      | { retryLabel?: never; onRetry?: never }
    ));

/**
 * Section descriptors: one arrangement placing typed elements in named slots.
 *
 * The arrangement key is `arrangement`, never `layout` (the report chapter
 * validator bans `layout`). A section carries no `kind` and no `title`, so it
 * never forms the `kind` + `id` + `title` triple the nested-descriptor guard
 * rejects; headings belong to the chapter and to copy elements. Geometry is
 * semantic only: the three split recipes, the grid column tokens, band tone
 * and width are enums the section renderer resolves to tokens.
 */
export const FM_SECTION_ARRANGEMENTS = [
  'prose',
  'split',
  'figures',
  'grid',
  'tabbed',
  'carousel',
  'band',
] as const;
export type FmSectionArrangement = (typeof FM_SECTION_ARRANGEMENTS)[number];

/**
 * Each tone has one job: `deep` states the answer, `light` is for reading,
 * `paper` holds working evidence (tables, ledgers, forms) one step off light so
 * the eye reads it as detail, and `dark` is the map and immersive canvas.
 */
export const FM_SECTION_TONES = ['light', 'dark', 'deep', 'paper'] as const;
export type FmSectionTone = (typeof FM_SECTION_TONES)[number];

/**
 * `full` is the band whose visual is the point: a band's background image
 * spans the viewport, and a split's media column runs out to the viewport edge
 * on its side while the copy stays on the content rail.
 */
export const FM_SECTION_WIDTHS = ['content', 'wide', 'full'] as const;
export type FmSectionWidth = (typeof FM_SECTION_WIDTHS)[number];

/**
 * The page's pace: `spacious` is the airy band that lets one statement breathe,
 * `compact` the dense band of a working table, `comfortable` the default. A
 * page alternates them for rhythm; a band never sets its own padding.
 */
export const FM_SECTION_DENSITIES = ['compact', 'comfortable', 'spacious'] as const;
export type FmSectionDensity = (typeof FM_SECTION_DENSITIES)[number];

/**
 * Where a lead copy's headline sits: `above` its body (default), or `beside`
 * it, the editorial split of a big headline on the start rail and the body on
 * the end rail.
 */
export const FM_SECTION_HEADLINES = ['above', 'beside'] as const;
export type FmSectionHeadline = (typeof FM_SECTION_HEADLINES)[number];

/**
 * A band image's scrim. `even` tints the whole image; `start`, `end` and
 * `bottom` fade from the band tone on the copy's side to the clear image, so a
 * photo keeps its colour where the copy is not. Below the desktop tier a
 * directional scrim becomes `bottom`.
 */
export const FM_BAND_SCRIMS = ['even', 'start', 'end', 'bottom'] as const;
export type FmBandScrim = (typeof FM_BAND_SCRIMS)[number];

/**
 * `cover` crops a photo to the band behind its scrim; `contain` places an
 * illustration whole on the end half, beside the copy and without a scrim.
 */
export const FM_BAND_IMAGE_FITS = ['cover', 'contain'] as const;
export type FmBandImageFit = (typeof FM_BAND_IMAGE_FITS)[number];

/**
 * `value` sections must show a real value; `decision` sections carry exactly
 * one primary action. Every section carries at most one primary action.
 */
export const FM_SECTION_INTENTS = ['value', 'decision'] as const;
export type FmSectionIntent = (typeof FM_SECTION_INTENTS)[number];

/** The `FmSplit` vocabulary; each balance is one `--fm-split-*` recipe token. */
export const FM_SPLIT_SIDES = ['start', 'end'] as const;
export const FM_SPLIT_BALANCES = ['balanced', 'media-dominant', 'copy-narrow'] as const;
export const FM_SPLIT_STACK_ORDERS = ['content-first', 'graphic-first'] as const;
export type FmSectionSplitSide = (typeof FM_SPLIT_SIDES)[number];
export type FmSectionSplitBalance = (typeof FM_SPLIT_BALANCES)[number];
export type FmSectionSplitStackOrder = (typeof FM_SPLIT_STACK_ORDERS)[number];

/** One `--fm-grid-<n>` token each; a numeric count would be geometry in a descriptor. */
export const FM_GRID_COLUMNS = ['two', 'three', 'four'] as const;
export type FmSectionGridColumns = (typeof FM_GRID_COLUMNS)[number];

const LEAD = ['copy'] as const;
const READING = [
  'copy',
  'keyFigures',
  'facts',
  'definitionList',
  'chart',
  'table',
  'rankedList',
  'steps',
  'timeline',
  'notice',
  'actions',
  'provenance',
  'lens',
] as const;
const SPLIT_CONTENT = [
  'copy',
  'keyFigures',
  'facts',
  'definitionList',
  'rankedList',
  'table',
  'steps',
  'timeline',
  'notice',
  'actions',
  'provenance',
  'lens',
] as const;
const SPLIT_MEDIA = ['chart', 'map', 'image', 'document', 'table', 'rankedList', 'timeline', 'lens'] as const;
const GRID_ITEMS = [
  'copy',
  'keyFigures',
  'facts',
  'chart',
  'map',
  'image',
  'document',
  'table',
  'rankedList',
  'notice',
] as const;
const PANEL = [
  'copy',
  'keyFigures',
  'facts',
  'definitionList',
  'chart',
  'map',
  'image',
  'document',
  'table',
  'rankedList',
  'steps',
  'timeline',
  'notice',
  'actions',
  'provenance',
] as const;
const SLIDE = ['copy', 'keyFigures', 'chart', 'map', 'image', 'actions'] as const;

interface FmSlotSpec {
  required: boolean;
  elements: readonly FmElementType[];
}

/**
 * Required and allowed slots per arrangement, with the element types each slot
 * accepts. Prose keeps map, image and document out: media-led content belongs in a
 * split, grid or band. Tabbed and carousel panels use `PANEL` / `SLIDE`; a
 * lens never sits in a tab panel because the tabs already switch.
 */
export const FM_SECTION_SLOTS = {
  prose: { content: { required: true, elements: READING } },
  split: {
    content: { required: true, elements: SPLIT_CONTENT },
    media: { required: true, elements: SPLIT_MEDIA },
  },
  figures: {
    lead: { required: false, elements: LEAD },
    figures: { required: true, elements: ['keyFigures'] },
    support: {
      required: false,
      // Text answers beside the headline numbers (a species, a status, a date)
      // are facts here, because a key figure is always a number.
      elements: ['facts', 'chart', 'steps', 'actions', 'provenance', 'notice'],
    },
  },
  grid: {
    lead: { required: false, elements: LEAD },
    items: { required: true, elements: GRID_ITEMS },
  },
  tabbed: { lead: { required: false, elements: LEAD } },
  carousel: { lead: { required: false, elements: LEAD } },
  band: {
    content: { required: true, elements: LEAD },
    actions: { required: false, elements: ['actions'] },
    background: { required: false, elements: ['image'] },
  },
} as const satisfies Record<FmSectionArrangement, Record<string, FmSlotSpec>>;

export const FM_SECTION_PANEL_ELEMENTS = { tabbed: PANEL, carousel: SLIDE } as const;

type SlotSpecs = typeof FM_SECTION_SLOTS;
type SlotElements<Spec extends FmSlotSpec> = readonly [
  FmElementOf<Spec['elements'][number]>,
  ...FmElementOf<Spec['elements'][number]>[],
];
type RequiredSlotKeys<Specs> = {
  [Key in keyof Specs]: Specs[Key] extends { required: true } ? Key : never;
}[keyof Specs];

export type FmSectionSlots<Arrangement extends FmSectionArrangement> = {
  [Key in RequiredSlotKeys<SlotSpecs[Arrangement]>]: SlotSpecs[Arrangement][Key] extends FmSlotSpec
    ? SlotElements<SlotSpecs[Arrangement][Key]>
    : never;
} & {
  [Key in Exclude<
    keyof SlotSpecs[Arrangement],
    RequiredSlotKeys<SlotSpecs[Arrangement]>
  >]?: SlotSpecs[Arrangement][Key] extends FmSlotSpec
    ? SlotElements<SlotSpecs[Arrangement][Key]>
    : never;
};

type PanelElement<Arrangement extends 'tabbed' | 'carousel'> = FmElementOf<
  (typeof FM_SECTION_PANEL_ELEMENTS)[Arrangement][number]
>;

/**
 * One tab or slide. `key` is stable across renders; `label` names it for the
 * tab list or "n of m"; `testId` keeps a route-supplied id on the panel.
 */
export interface FmSectionPanel<Arrangement extends 'tabbed' | 'carousel'> {
  key: string;
  label: string;
  testId?: string;
  elements: readonly [PanelElement<Arrangement>, ...PanelElement<Arrangement>[]];
}

/** Carousel chrome copy the route supplies; `slide` carries `{index}` and `{count}`. */
export interface FmSectionCarouselLabels {
  carousel: string;
  previous: string;
  next: string;
  slide: string;
}

type PanelList<Arrangement extends 'tabbed' | 'carousel'> = readonly [
  FmSectionPanel<Arrangement>,
  FmSectionPanel<Arrangement>,
  ...FmSectionPanel<Arrangement>[],
];

interface FmSectionArrangementOptions {
  prose: { headline?: FmSectionHeadline };
  split: {
    side?: FmSectionSplitSide;
    balance?: FmSectionSplitBalance;
    stackOrder?: FmSectionSplitStackOrder;
    /** Keeps the media side in view above the split breakpoint. */
    sticky?: boolean;
  };
  figures: object;
  grid: { columns: FmSectionGridColumns };
  tabbed: {
    /** Accessible name of the tab list. */
    label: string;
    panels: PanelList<'tabbed'>;
  };
  carousel: { labels: FmSectionCarouselLabels; slides: PanelList<'carousel'> };
  band: { scrim?: FmBandScrim; imageFit?: FmBandImageFit };
}

type FmSectionReadyState = Extract<FmSectionState, { status: 'ready' | 'partial' | 'stale' }>;
type FmSectionUnavailableState = Exclude<FmSectionState, FmSectionReadyState>;

/** Panel keys and the copy that names them exist only while the section shows content. */
type PanelKeys = 'panels' | 'slides' | 'label' | 'labels';
type ContentKeys<Arrangement extends FmSectionArrangement> =
  | 'slots'
  | Extract<keyof FmSectionArrangementOptions[Arrangement], PanelKeys>;
type WithoutKeys<Keys extends PropertyKey> = { [Key in Keys]?: never };

/** The validator rejects an empty notice list, so the type admits only a non-empty one. */
export type FmSectionNotices = readonly [FmNoticeElement, ...FmNoticeElement[]];

interface FmSectionBase<Arrangement extends FmSectionArrangement> {
  /** Unique within the chapter; renderers key and derive test ids from it. */
  id: string;
  arrangement: Arrangement;
  tone?: FmSectionTone;
  width?: FmSectionWidth;
  /** Band padding; an embedded section takes its host's pace instead. */
  density?: FmSectionDensity;
  intent?: FmSectionIntent;
  testId?: string;
  /** Data-gated advisories shown above the section in every state; absent when there are none. */
  notices?: FmSectionNotices;
}

/**
 * Today's state union unchanged: ready, partial and stale carry the slots
 * (partial and stale add a section-level notice); loading, empty, locked and
 * error replace the content, so they carry no slots. An unavailable section
 * may keep its `lead` copy: the heading and lede still say what the section is
 * about while the state notice says why its content is missing.
 */
type FmSectionOf<Arrangement extends FmSectionArrangement> = FmSectionBase<Arrangement> &
  Omit<FmSectionArrangementOptions[Arrangement], PanelKeys> &
  (
    | ({ state: FmSectionReadyState; slots: FmSectionSlots<Arrangement> } & Pick<
        FmSectionArrangementOptions[Arrangement],
        Extract<keyof FmSectionArrangementOptions[Arrangement], PanelKeys>
      >)
    | ({ state: FmSectionUnavailableState; lead?: FmCopyElement } & WithoutKeys<
        ContentKeys<Arrangement>
      >)
  );

export type FmProseSection = FmSectionOf<'prose'>;
export type FmSplitSectionDescriptor = FmSectionOf<'split'>;
export type FmFiguresSection = FmSectionOf<'figures'>;
export type FmGridSection = FmSectionOf<'grid'>;
export type FmTabbedSection = FmSectionOf<'tabbed'>;
export type FmCarouselSection = FmSectionOf<'carousel'>;
export type FmBandSection = FmSectionOf<'band'>;

export type FmSection =
  | FmProseSection
  | FmSplitSectionDescriptor
  | FmFiguresSection
  | FmGridSection
  | FmTabbedSection
  | FmCarouselSection
  | FmBandSection;

export type { FmSectionReadyState };
